import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
  SilentRequest,
} from '@azure/msal-browser';
import { getQueryString, getUrlPart } from '@nx-ng/util/parse-query-string';
import { isEqual, maxBy } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { silentGetLoginTokenRequest } from './b2c-config';

@Injectable({ providedIn: 'root' })
export class B2CEventsService {
  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router
  ) {}

  private isLoggedIn: BehaviorSubject<boolean | undefined> =
    new BehaviorSubject<boolean | undefined>(undefined);
  private state: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public isLoggedIn$ = this.isLoggedIn.asObservable();

  private activeAccount: BehaviorSubject<AccountInfo | undefined> =
    new BehaviorSubject<AccountInfo | undefined>(undefined);
  public activeAccount$ = this.activeAccount.asObservable();
  public state$ = this.state.asObservable();

  private redirectPath: BehaviorSubject<string | undefined> =
    new BehaviorSubject<string | undefined>(undefined);
  public redirectPath$ = this.redirectPath.asObservable();

  private redirectQueryString: BehaviorSubject<string | undefined> =
    new BehaviorSubject<string | undefined>(undefined);
  public redirectQueryString$ = this.redirectQueryString.asObservable();

  public userName$ = this.activeAccount$.pipe(
    map((account: AccountInfo | undefined) => {
      return account?.username || '';
    })
  );

  private readonly _destroying$ = new Subject<void>();

  init() {
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          const activeAccount = this.checkAndSetActiveAccount();
          this.setLoginDisplay(activeAccount);
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        const activeAccount = this.checkAndSetActiveAccount();
        this.setLoginDisplay(activeAccount);
      });

    this.authService.handleRedirectObservable().subscribe((res) => {
      if (res?.state) {
        this.state.next(res.state);

        const path = getUrlPart(res.state);
        const queryPart = getQueryString(res.state);

        this.redirectPath.next(path);
        this.redirectQueryString.next(queryPart);
      }
    });
  }

  setLoginDisplay(activeAccount: AccountInfo | undefined) {
    if (activeAccount == undefined) {
      this.isLoggedIn.next(false);
      return;
    }

    const request: SilentRequest = {
      ...silentGetLoginTokenRequest,
      account: activeAccount,
    };

    //Only set login state to true if we can acquire a token silently
    this.authService
      .acquireTokenSilent(request)
      .pipe(take(1))
      .subscribe(
        (res) => this.isLoggedIn.next(true),
        (err) => {
          this.authService.logoutRedirect(); //Found issue with log standing refresh token breaking things
        }
      );
  }

  checkAndSetActiveAccount(): AccountInfo | undefined {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();
    const accounts = this.authService.instance.getAllAccounts();

    const mostRecentAccount = maxBy(
      accounts,
      (account) => (account.idTokenClaims as any)?.auth_time
    );

    let newActiveAccount: AccountInfo | undefined = undefined;

    if (!activeAccount && accounts.length > 0) {
      if (mostRecentAccount) {
        this.authService.instance.setActiveAccount(mostRecentAccount);
        newActiveAccount = mostRecentAccount;
      } else {
        newActiveAccount = accounts[0];
      }
    } else if (activeAccount) {
      if (isEqual(mostRecentAccount, activeAccount)) {
        newActiveAccount = activeAccount;
      } else {
        newActiveAccount = mostRecentAccount;
      }
    }

    if (newActiveAccount) {
      this.activeAccount.next(newActiveAccount);
    }
    return newActiveAccount;
  }

  clearRedirectPath() {
    this.redirectPath.next(undefined);
  }
  destroy() {
    this._destroying$.next();
  }
}
